import { useParams, useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import {
  projetos,
  equipeDetalhada,
  relatoriosProjeto,
  qualidadeProjeto,
  vistoriasProjeto,
  alertasProjeto,
  atividadesDetalhadasProjeto,
} from "@/mocks/projetos";
import { individuos, estadoFitoConfig, riscoConfig, statusRevisaoConfig } from "@/mocks/cta";
import ProjectStatusBadge from "@/pages/projetos/components/ProjectStatusBadge";
import ProjectProgressBar from "@/pages/projetos/components/ProjectProgressBar";
import ProjectKpiCard from "@/pages/projetos/components/ProjectKpiCard";
import ProjectAlertsCard from "./components/ProjectAlertsCard";
import ProjectActivityTimeline from "./components/ProjectActivityTimeline";
import ProjectQualityPanel from "./components/ProjectQualityPanel";
import ProjectMapPreview from "./components/ProjectMapPreview";
import ProjectInspectionTable from "./components/ProjectInspectionTable";
import ProjectTeamCards from "./components/ProjectTeamCards";
import ProjectReportsGrid from "./components/ProjectReportsGrid";
import ProjectSettingsPanel from "./components/ProjectSettingsPanel";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";

const tabs = [
  { id: "visao-geral", label: "Visão Geral", icon: "ri-dashboard-line" },
  { id: "arvores", label: "CTA Arbóreo", icon: "ri-plant-line" },
  { id: "mapa", label: "Mapa", icon: "ri-map-2-line" },
  { id: "vistorias", label: "Vistorias", icon: "ri-clipboard-line" },
  { id: "equipe", label: "Equipe", icon: "ri-team-line" },
  { id: "documentos", label: "Documentos e Fontes", icon: "ri-file-list-3-line" },
  { id: "relatorios", label: "Relatórios", icon: "ri-bar-chart-box-line" },
  { id: "configuracoes", label: "Configurações", icon: "ri-settings-3-line" },
];

const docStatusConfig: Record<string, { badge: string; label: string }> = {
  aprovado: { badge: "bg-primary-100 text-primary-700", label: "Aprovado" },
  pendente: { badge: "bg-warning-100 text-warning-700", label: "Pendente" },
  revisao: { badge: "bg-caution-100 text-caution-700", label: "Em Revisão" },
};

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("visao-geral");
  const [showMoreActions, setShowMoreActions] = useState(false);

  const projeto = projetos.find((p) => p.id === id);

  if (!projeto) {
    return (
      <div className="bg-white rounded-card border border-background-200 p-12 text-center">
        <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
          <i className="ri-folder-chart-line text-foreground-400 text-2xl"></i>
        </div>
        <h3 className="text-lg font-semibold text-foreground-900 mb-2">Projeto não encontrado</h3>
        <p className="text-sm text-foreground-500 mb-4">O projeto solicitado não existe ou foi removido.</p>
        <button onClick={() => navigate("/projetos")} className="btn-primary cursor-pointer">
          <i className="ri-arrow-left-line"></i>
          Voltar para Projetos
        </button>
      </div>
    );
  }

  // Filter trees belonging to this project
  const arvoresProjeto = individuos.filter((ind) => ind.projeto === projeto.nome);

  const fitossanitarioData = [
    { name: "Bom", value: Math.round(projeto.totalArvores * 0.66), color: "#1B6E4F" },
    { name: "Regular", value: Math.round(projeto.totalArvores * 0.23), color: "#F57C00" },
    { name: "Ruim", value: Math.round(projeto.totalArvores * 0.07), color: "#D32F2F" },
    { name: "Crítico", value: projeto.riscoCritico, color: "#8D1F1F" },
    { name: "Morta", value: Math.max(0, Math.round(projeto.totalArvores * 0.012)), color: "#3F4A52" },
  ];

  const riscoData = [
    { name: "Baixo", value: Math.round(projeto.totalArvores * 0.57), color: "#1B6E4F" },
    { name: "Médio", value: Math.round(projeto.totalArvores * 0.31), color: "#FBC02D" },
    { name: "Alto", value: Math.round(projeto.totalArvores * 0.11), color: "#F57C00" },
    { name: "Crítico", value: projeto.riscoCritico, color: "#D32F2F" },
  ];

  const evolucaoMensal = [
    { mes: "Jan", cadastros: 0, vistorias: 0 },
    { mes: "Fev", cadastros: 120, vistorias: 80 },
    { mes: "Mar", cadastros: 340, vistorias: 260 },
    { mes: "Abr", cadastros: 560, vistorias: 410 },
    { mes: "Mai", cadastros: 780, vistorias: 580 },
    { mes: "Jun", cadastros: 940, vistorias: 742 },
  ];

  const qualityMetrics = [
    { label: "Coordenada válida", value: qualidadeProjeto.coordenadaValida, icon: "ri-map-pin-line" },
    { label: "Com foto anexada", value: qualidadeProjeto.comFoto, icon: "ri-camera-line" },
    { label: "Espécie identificada", value: qualidadeProjeto.especieIdentificada, icon: "ri-leaf-line" },
    { label: "Vistoria validada", value: qualidadeProjeto.vistoriaValidada, icon: "ri-check-double-line" },
    { label: "Responsável técnico", value: qualidadeProjeto.responsavelTecnico, icon: "ri-user-star-line" },
    { label: "Recomendação preenchida", value: qualidadeProjeto.recomendacaoPreenchida, icon: "ri-file-text-line" },
  ];

  const indiceGeralQuality = Math.round(
    Object.values(qualidadeProjeto).reduce((a, b) => a + b, 0) / Object.values(qualidadeProjeto).length
  );

  const vistoriasConcluidas = vistoriasProjeto.filter((v) => v.status === "concluída").length;
  const vistoriasPendentes = vistoriasProjeto.filter((v) => v.status === "pendente").length;
  const vistoriasEmRevisao = vistoriasProjeto.filter((v) => v.status === "em revisão").length;
  const vistoriasVencidas = vistoriasProjeto.filter((v) => v.status === "vencida").length;

  return (
    <div className="space-y-4">
      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-foreground-500">
        <Link to="/" className="hover:text-foreground-700 transition-colors">Dashboard</Link>
        <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
        <Link to="/projetos" className="hover:text-foreground-700 transition-colors">Projetos</Link>
        <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
        <span className="text-foreground-400">Detalhe do Projeto</span>
      </nav>

      {/* Header */}
      <div className="bg-white rounded-card border border-background-200 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="text-h4 font-bold text-foreground-900 font-heading">{projeto.nome}</h1>
              <ProjectStatusBadge status={projeto.status} />
            </div>
            <p className="text-sm text-foreground-500">
              {projeto.id} · {projeto.tipo} · {projeto.municipio}/{projeto.uf}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            <button className="btn-outline text-xs px-3 py-2 cursor-pointer">
              <i className="ri-edit-line"></i>
              Editar projeto
            </button>
            <button onClick={() => navigate("/cta")} className="btn-primary text-xs px-3 py-2 cursor-pointer">
              <i className="ri-add-line"></i>
              Nova árvore
            </button>
            <button onClick={() => navigate("/mapa")} className="btn-outline text-xs px-3 py-2 cursor-pointer">
              <i className="ri-map-2-line"></i>
              Abrir mapa
            </button>
            <button className="btn-outline text-xs px-3 py-2 cursor-pointer">
              <i className="ri-file-chart-line"></i>
              Gerar relatório
            </button>
            <div className="relative">
              <button
                onClick={() => setShowMoreActions(!showMoreActions)}
                className="btn-outline text-xs px-2 py-2 cursor-pointer"
              >
                <i className="ri-more-line"></i>
              </button>
              {showMoreActions && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMoreActions(false)}></div>
                  <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-lg border border-background-200 shadow-sm z-20 py-1">
                    <button onClick={() => setShowMoreActions(false)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer">
                      <i className="ri-download-line text-foreground-400"></i>Exportar dados
                    </button>
                    <button onClick={() => setShowMoreActions(false)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer">
                      <i className="ri-file-copy-line text-foreground-400"></i>Duplicar projeto
                    </button>
                    <button onClick={() => setShowMoreActions(false)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-danger-600 hover:bg-danger-50 transition-colors cursor-pointer">
                      <i className="ri-archive-line"></i>Arquivar projeto
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-card border border-background-200 p-4">
          <p className="text-xs text-foreground-500 font-medium mb-1">Cliente / Contratante</p>
          <div className="flex items-center gap-1.5">
            <i className="ri-building-line text-foreground-400 text-sm"></i>
            <p className="text-sm font-medium text-foreground-900">{projeto.cliente}</p>
          </div>
        </div>
        <div className="bg-white rounded-card border border-background-200 p-4">
          <p className="text-xs text-foreground-500 font-medium mb-1">Responsável Técnico</p>
          <div className="flex items-center gap-1.5">
            <i className="ri-user-star-line text-foreground-400 text-sm"></i>
            <p className="text-sm font-medium text-foreground-900">{projeto.responsavel}</p>
          </div>
        </div>
        <div className="bg-white rounded-card border border-background-200 p-4">
          <p className="text-xs text-foreground-500 font-medium mb-1">Período</p>
          <div className="flex items-center gap-1.5">
            <i className="ri-calendar-line text-foreground-400 text-sm"></i>
            <p className="text-sm text-foreground-900">
              {new Date(projeto.dataInicio).toLocaleDateString("pt-BR")} → {new Date(projeto.dataPrevisao).toLocaleDateString("pt-BR")}
              {projeto.dataConclusao && (
                <span className="text-primary-600 font-medium ml-2">
                  Concluído: {new Date(projeto.dataConclusao).toLocaleDateString("pt-BR")}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="bg-white rounded-card border border-background-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-foreground-900">Progresso do Projeto</p>
          <span className="text-sm font-bold text-foreground-900 font-heading tabular-nums">{projeto.progresso}%</span>
        </div>
        <ProjectProgressBar progresso={projeto.progresso} size="md" showLabel={false} />
        <div className="flex items-center justify-between mt-2 text-xs text-foreground-500 flex-wrap gap-x-4">
          <span>Área: {projeto.areaAbrangencia}</span>
          <span>Metodologia: {projeto.metodologia}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-card border border-background-200 overflow-hidden">
        <div className="flex items-center gap-0 border-b border-background-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                activeTab === tab.id
                  ? "text-primary-700 border-b-2 border-primary-500 bg-primary-50/50"
                  : "text-foreground-500 hover:text-foreground-700 hover:bg-background-50"
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {/* Tab 1: Visão Geral */}
          {activeTab === "visao-geral" && (
            <div className="space-y-6">
              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <ProjectKpiCard label="Árvores Cadastradas" value={projeto.totalArvores} icon="ri-plant-line" iconColor="text-primary-600" iconBg="bg-primary-50" sublabel={`${projeto.arvoresRevisao} pendentes de revisão`} />
                <ProjectKpiCard label="Vistorias Realizadas" value={projeto.vistoriasRealizadas} icon="ri-check-double-line" iconColor="text-accent-600" iconBg="bg-accent-50" sublabel={`${projeto.vistoriasPendentes} pendentes`} />
                <ProjectKpiCard label="Risco Crítico" value={projeto.riscoCritico} icon="ri-alert-line" iconColor="text-danger-600" iconBg="bg-danger-50" sublabel="Necessita manejo emergencial" />
                <ProjectKpiCard label="CO₂e Estimado" value={`${projeto.co2eEstimado.toLocaleString("pt-BR")} t`} icon="ri-cloud-line" iconColor="text-primary-600" iconBg="bg-primary-50" sublabel="Sequestro preliminar" />
                <ProjectKpiCard label="Espécies Identificadas" value={projeto.especiesIdentificadas} icon="ri-leaf-line" iconColor="text-accent-600" iconBg="bg-accent-50" />
                <ProjectKpiCard label="Fotos Anexadas" value={projeto.fotosAnexadas.toLocaleString("pt-BR")} icon="ri-camera-line" iconColor="text-secondary-600" iconBg="bg-secondary-50" />
                <ProjectKpiCard label="Área Inventariada" value={`${projeto.areaInventariada} ha`} icon="ri-map-pin-line" iconColor="text-primary-600" iconBg="bg-primary-50" />
                <ProjectKpiCard label="Árvores em Revisão" value={projeto.arvoresRevisao} icon="ri-edit-line" iconColor="text-warning-600" iconBg="bg-warning-50" />
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">Evolução Mensal de Cadastros</h3>
                  <div className="h-[180px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={evolucaoMensal}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E8EAED" />
                        <XAxis dataKey="mes" tick={{ fontSize: 10, fill: "#6B737A" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: "#6B737A" }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #E8EAED", fontSize: "12px" }} />
                        <Bar dataKey="cadastros" fill="#1B6E4F" radius={[3, 3, 0, 0]} barSize={10} name="Cadastros" />
                        <Bar dataKey="vistorias" fill="#0052CC" radius={[3, 3, 0, 0]} barSize={10} name="Vistorias" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">Estado Fitossanitário</h3>
                  <div className="h-[180px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={fitossanitarioData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="value">
                          {fitossanitarioData.map((entry, i) => (
                            <Cell key={`fito-${i}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => value.toLocaleString("pt-BR")} contentStyle={{ borderRadius: "8px", border: "1px solid #E8EAED", fontSize: "12px" }} />
                        <Legend iconType="circle" iconSize={7} formatter={(value: string) => <span className="text-[10px] text-foreground-600">{value}</span>} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">Classificação de Risco</h3>
                  <div className="h-[180px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={riscoData} layout="vertical" margin={{ left: 0, right: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E8EAED" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 10, fill: "#6B737A" }} axisLine={false} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#454B50" }} width={50} axisLine={false} tickLine={false} />
                        <Tooltip formatter={(value: number) => value.toLocaleString("pt-BR")} contentStyle={{ borderRadius: "8px", border: "1px solid #E8EAED", fontSize: "12px" }} />
                        <Bar dataKey="value" radius={[0, 3, 3, 0]} barSize={14}>
                          {riscoData.map((entry, i) => (
                            <Cell key={`rsk-${i}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Alerts + Quality */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">Alertas do Projeto</h3>
                  <ProjectAlertsCard alertas={alertasProjeto} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">Qualidade do Cadastro</h3>
                  <ProjectQualityPanel metrics={qualityMetrics} indiceGeral={indiceGeralQuality} />
                </div>
              </div>

              {/* Activity Timeline */}
              <div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">Últimas Atividades</h3>
                <ProjectActivityTimeline atividades={atividadesDetalhadasProjeto} />
              </div>
            </div>
          )}

          {/* Tab 2: CTA Arbóreo */}
          {activeTab === "arvores" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-sm font-semibold text-foreground-900 font-heading">
                  Árvores Vinculadas ao Projeto
                  <span className="ml-2 text-xs font-normal text-foreground-500">({arvoresProjeto.length} de {projeto.totalArvores.toLocaleString("pt-BR")} total)</span>
                </h3>
                <div className="flex items-center gap-2">
                  <button onClick={() => navigate("/cta")} className="btn-primary text-xs px-3 py-2 cursor-pointer">
                    <i className="ri-add-line"></i>
                    Nova árvore
                  </button>
                  <button className="btn-outline text-xs px-3 py-2 cursor-pointer">
                    <i className="ri-upload-line"></i>
                    Importar planilha
                  </button>
                  <button className="btn-outline text-xs px-3 py-2 cursor-pointer">
                    <i className="ri-download-line"></i>
                    Exportar
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto border border-background-200 rounded-lg">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-background-200 bg-background-50">
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Código CTA</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Espécie</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Bairro/Logradouro</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Estado Fito.</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Risco</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Recomendação</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Última Vistoria</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Status</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {arvoresProjeto.map((arv) => {
                      const fito = estadoFitoConfig[arv.estadoFitossanitario] || { badge: "bg-background-100 text-foreground-500", label: arv.estadoFitossanitario };
                      const rsk = riscoConfig[arv.classeRisco] || { badge: "bg-background-100 text-foreground-500", label: arv.classeRisco };
                      const st = statusRevisaoConfig[arv.statusRevisao] || { badge: "bg-background-100 text-foreground-500", label: arv.statusRevisao };
                      const numericId = arv.codigoCta.split("-").pop() || "";
                      return (
                        <tr key={arv.id} className="border-b border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-4 py-3">
                            <span className="text-xs font-mono text-foreground-800 whitespace-nowrap">{arv.codigoCta}</span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-foreground-900 whitespace-nowrap">{arv.nomePopular}</p>
                            <p className="text-xs text-foreground-400 italic">{arv.especie}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-foreground-600">{arv.bairro} / {arv.logradouro}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`badge ${fito.badge}`}>{fito.label}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`badge ${rsk.badge}`}>{rsk.label}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-foreground-600 max-w-[140px] truncate">{arv.recomendacao}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-foreground-700 whitespace-nowrap">{new Date(arv.ultimaVistoria).toLocaleDateString("pt-BR")}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`badge ${st.badge}`}>{st.label}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => navigate(`/cta/${numericId}`)}
                                className="w-7 h-7 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer"
                                title="Ver FCA"
                              >
                                <i className="ri-eye-line text-xs"></i>
                              </button>
                              <button className="w-7 h-7 rounded-md bg-secondary-50 text-secondary-600 flex items-center justify-center hover:bg-secondary-100 transition-colors cursor-pointer" title="Editar">
                                <i className="ri-edit-line text-xs"></i>
                              </button>
                              <button className="w-7 h-7 rounded-md bg-accent-50 text-accent-600 flex items-center justify-center hover:bg-accent-100 transition-colors cursor-pointer" title="Abrir no mapa">
                                <i className="ri-map-pin-line text-xs"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {arvoresProjeto.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-2xl bg-primary-50 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-plant-line text-primary-500 text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-2">Nenhuma árvore cadastrada</h3>
                  <p className="text-sm text-foreground-500 mb-4">Este projeto ainda não possui árvores cadastradas.</p>
                  <button onClick={() => navigate("/cta")} className="btn-primary cursor-pointer">
                    <i className="ri-add-line"></i>
                    Cadastrar primeira árvore
                  </button>
                </div>
              )}

              {arvoresProjeto.length > 0 && arvoresProjeto.length < projeto.totalArvores && (
                <p className="text-xs text-foreground-500 text-center">
                  Exibindo {arvoresProjeto.length} árvores do mock. As {projeto.totalArvores.toLocaleString("pt-BR")} árvores estarão disponíveis com integração ao backend.
                </p>
              )}
            </div>
          )}

          {/* Tab 3: Mapa */}
          {activeTab === "mapa" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground-900 font-heading">Mapa do Projeto</h3>
                <button onClick={() => navigate("/mapa")} className="btn-primary text-xs px-3 py-2 cursor-pointer">
                  <i className="ri-map-2-line"></i>
                  Abrir mapa completo
                </button>
              </div>
              <ProjectMapPreview
                municipio={projeto.municipio}
                uf={projeto.uf}
                areaAbrangencia={projeto.areaAbrangencia}
              />
            </div>
          )}

          {/* Tab 4: Vistorias */}
          {activeTab === "vistorias" && (
            <div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">Vistorias do Projeto</h3>
              <ProjectInspectionTable
                vistorias={vistoriasProjeto}
                concluidas={vistoriasConcluidas}
                pendentes={vistoriasPendentes}
                emRevisao={vistoriasEmRevisao}
                vencidas={vistoriasVencidas}
              />
            </div>
          )}

          {/* Tab 5: Equipe */}
          {activeTab === "equipe" && (
            <div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">Equipe Técnica do Projeto</h3>
              <ProjectTeamCards membros={equipeDetalhada} />
            </div>
          )}

          {/* Tab 6: Documentos e Fontes */}
          {activeTab === "documentos" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground-900 font-heading">Documentos e Fontes</h3>
                <button className="btn-primary text-xs px-3 py-2 cursor-pointer">
                  <i className="ri-add-line"></i>
                  Adicionar documento
                </button>
              </div>
              <div className="overflow-x-auto border border-background-200 rounded-lg">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-background-200 bg-background-50">
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Documento</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Tipo</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Número</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Data</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Status</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Tamanho</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { id: "DOC-001", nome: "Termo de Abertura do Projeto", tipo: "termo", numero: "TAP-2026-005", data: "2026-04-10", status: "aprovado", tamanho: "2.4 MB" },
                      { id: "DOC-002", nome: "Metodologia do Inventário - CTA FloraIQ v2.1", tipo: "metodologia", numero: "MET-CTA-2026", data: "2026-04-12", status: "aprovado", tamanho: "8.7 MB" },
                      { id: "DOC-003", nome: "Base Cartográfica - Goiânia Centro (Shapefile)", tipo: "cartografia", numero: "GIS-2026-045", data: "2026-04-15", status: "pendente", tamanho: "45.2 MB" },
                      { id: "DOC-004", nome: "Relatório Parcial - Inventário Abr/2026", tipo: "relatorio", numero: "REL-PAR-2026-01", data: "2026-05-30", status: "revisao", tamanho: "12.1 MB" },
                      { id: "DOC-005", nome: "Fotos de Campo - Lote 01 ao 15", tipo: "fotos", numero: "FOT-2026-015", data: "2026-06-15", status: "aprovado", tamanho: "1.2 GB" },
                      { id: "DOC-006", nome: "Autorização Ambiental - AMMA 2026/0458", tipo: "autorizacao", numero: "AMMA-0458/2026", data: "2026-04-08", status: "aprovado", tamanho: "1.8 MB" },
                      { id: "DOC-007", nome: "Laudo Fitossanitário - Setor Marista", tipo: "laudo", numero: "LF-2026-032", data: "2026-06-20", status: "revisao", tamanho: "5.3 MB" },
                      { id: "DOC-008", nome: "Ordem de Serviço - Campo Fase 2", tipo: "ordem", numero: "OS-2026-089", data: "2026-06-01", status: "aprovado", tamanho: "0.8 MB" },
                    ].map((doc) => {
                      const stCfg = docStatusConfig[doc.status];
                      const tipoLabel: Record<string, string> = {
                        termo: "Termo",
                        metodologia: "Metodologia",
                        cartografia: "Cartografia",
                        relatorio: "Relatório",
                        fotos: "Fotos",
                        autorizacao: "Autorização",
                        laudo: "Laudo",
                        ordem: "Ordem",
                      };
                      return (
                        <tr key={doc.id} className="border-b border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <i className="ri-file-line text-foreground-400"></i>
                              <span className="text-sm font-medium text-foreground-900">{doc.nome}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-foreground-600 bg-background-100 px-2 py-0.5 rounded-md uppercase whitespace-nowrap">{tipoLabel[doc.tipo] || doc.tipo}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs font-mono text-foreground-600 whitespace-nowrap">{doc.numero}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-foreground-600 whitespace-nowrap">{new Date(doc.data).toLocaleDateString("pt-BR")}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`badge ${stCfg.badge}`}>{stCfg.label}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-foreground-600 tabular-nums">{doc.tamanho}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button className="w-7 h-7 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer" title="Download">
                                <i className="ri-download-line text-xs"></i>
                              </button>
                              <button className="w-7 h-7 rounded-md bg-secondary-50 text-secondary-600 flex items-center justify-center hover:bg-secondary-100 transition-colors cursor-pointer" title="Visualizar">
                                <i className="ri-eye-line text-xs"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Tab 7: Relatórios */}
          {activeTab === "relatorios" && (
            <div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">Relatórios do Projeto</h3>
              <ProjectReportsGrid relatorios={relatoriosProjeto} />
            </div>
          )}

          {/* Tab 8: Configurações */}
          {activeTab === "configuracoes" && (
            <div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">Configurações do Projeto</h3>
              <ProjectSettingsPanel projeto={projeto} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}